import { z } from "zod";
import { apiListSchema } from "./list";
import { mapDtoToService, serviceDtoSchema, serviceSchema } from "./services";
import {
  mapProviderFromDto,
  ProviderDtoSchema,
  ProviderSchema,
} from "./provider";
import { RegionDtoSchema, regionFromDto, RegionSchema } from "./region";
import { PaymentModel } from "./enums";

//? DTO
export const CreateServiceProviderDTOSchema = z.object({
  RegionId: z.number().nullish(),
  RequiresPrice: z.boolean().nullish(),
  Price: z.number().nullish(),
  Rating: z.number(),
  ServiceId: z.number(),
  ProviderId: z.number(),
  PaymentModel: z.number().nullish()
});
export type CreateServiceProviderDTO = z.infer<
  typeof CreateServiceProviderDTOSchema
>;

export const ServiceProviderDTOSchema = CreateServiceProviderDTOSchema.extend({
  Id: z.number(),
  Service: serviceDtoSchema.nullish(),
  Provider: ProviderDtoSchema.nullish(),
  Region: RegionDtoSchema.nullish(),
});

export type ServiceProviderDTO = z.infer<typeof ServiceProviderDTOSchema>;

export const serviceProviderDtoListSchema = apiListSchema(
  ServiceProviderDTOSchema
);

//? Model

export const CreateServiceProviderSchema = z.object({
  serviceId: z.string().min(1),
  providerId: z.string().min(1),
  rating: z.number().min(0),
  requiresPrice: z.boolean().nullish(),
  price: z.number().min(0).nullish(),
  regionId: z.string().nullish(),
  paymentModel: z.nativeEnum(PaymentModel).nullish()
});

export const ServiceProviderSchema = CreateServiceProviderSchema.extend({
  id: z.string().min(1),
  region: RegionSchema.nullish(),
  service: serviceSchema.nullish(),
  provider: ProviderSchema.nullish(),
});

export type ServiceProvider = z.infer<typeof ServiceProviderSchema>;
export type CreateServiceProvider = z.infer<typeof CreateServiceProviderSchema>;
export type ServiceProviderDomain = "service" | "provider";

//? Mappers

export function serviceProviderFromDto(
  dto: ServiceProviderDTO
): ServiceProvider {
  return {
    id: dto.Id.toString(),
    providerId: dto.ProviderId.toString(),
    serviceId: dto.ServiceId.toString(),
    rating: dto.Rating,
    requiresPrice: Boolean(dto.RequiresPrice),
    paymentModel: dto.PaymentModel,
    price: dto.Price ?? null,
    regionId: dto.RegionId?.toString(),
    service: dto.Service ? mapDtoToService(dto.Service) : undefined,
    provider: dto.Provider ? mapProviderFromDto(dto.Provider) : undefined,
    region: dto.Region ? regionFromDto(dto.Region) : undefined,
  };
}

export function serviceProviderToDto(dto: ServiceProvider): ServiceProviderDTO {
  return {
    Id: Number.parseInt(dto.id),
    ProviderId: Number.parseInt(dto.providerId),
    ServiceId: Number.parseInt(dto.serviceId),
    Rating: dto.rating,
    RequiresPrice: dto.requiresPrice,
    Price: dto.price,
    PaymentModel: dto.paymentModel,
    RegionId: dto.regionId ? Number.parseInt(dto.regionId) : undefined,
  };
}

export function createServiceProviderToDto(
  dto: CreateServiceProvider
): CreateServiceProviderDTO {
  return {
    ProviderId: Number.parseInt(dto.providerId),
    Price: dto.price,
    Rating: dto.rating,
    RegionId: dto.regionId ? Number.parseInt(dto.regionId) : undefined,
    RequiresPrice: dto.requiresPrice,
    PaymentModel: dto.paymentModel,
    ServiceId: Number.parseInt(dto.serviceId),
  };
}
